import { MAX_PERIOD_DAYS } from '../GscTools/period';
import { emptyDraft, resolveScope } from '../GscTools/scope';
import { SCOPE_KEYS, hasScope, scopeDraftFromParams, scopeParamsFromScope } from '../GscTools/url';
import {
    SEGMENT,
    SEGMENTS,
    pagePrefixError,
    pagePrefixRegex,
    segmentTakesFilters,
} from './segments';

/**
 * The scope this report runs under: the shared one, plus which dimension the traffic is cut by
 * and an optional page prefix to read it inside.
 *
 * Two things here are not the user's choice and are reported rather than applied silently. A
 * report keyed by search appearance drops the country, device and page filters, because Search
 * Console will not combine that dimension with anything else — so the table is of the whole
 * property and says so. And a page filter changes what an aggregated row counts: Search Console
 * aggregates by page once a page filter is in force, so the figures stop being property totals.
 * Both come back as warnings the scope bar shows above the Apply button.
 */
export function initialDraft(period) {
    return {
        ...emptyDraft(period),
        segment: SEGMENT.COUNTRY,
        pagePrefix: '',
    };
}

export function resolveReportScope(draft, t) {
    const scope = resolveScope(draft, t, { maxDays: MAX_PERIOD_DAYS });

    const segment = SEGMENTS.includes(draft.segment) ? draft.segment : SEGMENT.COUNTRY;
    const pagePrefix = (draft.pagePrefix ?? '').trim();

    const prefixError = pagePrefixError(pagePrefix, t);
    if (prefixError) scope.errors.push(prefixError);

    const pageRegex = prefixError ? null : pagePrefixRegex(pagePrefix);
    const takesFilters = segmentTakesFilters(segment);
    const droppedFilters = !takesFilters && !!(scope.country || scope.device || pageRegex);

    if (droppedFilters) {
        scope.warnings.push(t('tools.audience_appearance.warnings.appearance_alone'));
    }
    if (pageRegex && takesFilters) {
        scope.warnings.push(t('tools.audience_appearance.warnings.page_aggregation'));
    }

    return {
        ...scope,
        segment,
        pagePrefix,
        pageRegex,
        // Whether the segment table's own requests carry the scope's filters at all. Every block
        // reads this rather than re-deriving it, so block 01's rows and block 02's trend are
        // always read under exactly the same filters.
        takesFilters,
        droppedFilters,
    };
}

/** The query-string keys this report owns on top of the shared scope ones. */
const REPORT_KEYS = ['segment', 'page-prefix'];

export const URL_KEYS = [...SCOPE_KEYS, ...REPORT_KEYS];

/** Whether the address describes a report to run, counting this report's own controls too. */
export function hasReportScope(params) {
    return hasScope(params, REPORT_KEYS);
}

/**
 * The draft this report starts from when its address already describes one.
 *
 * A segment that is not one of the three is dropped rather than sent: the value becomes a
 * dimension in a request, and an unrecognised one would be a request Search Console rejects. A
 * page prefix is taken as text and validated by resolveReportScope like any typed one.
 */
export function reportDraftFromParams(params) {
    const draft = scopeDraftFromParams(params);

    if (SEGMENTS.includes(params.segment)) draft.segment = params.segment;
    if (params['page-prefix']) draft.pagePrefix = params['page-prefix'];

    return draft;
}

/** The applied scope as query parameters. */
export function reportParamsFromScope(scope) {
    return {
        ...scopeParamsFromScope(scope),
        segment: scope.segment,
        'page-prefix': scope.pagePrefix || undefined,
    };
}
